// AI Captions Service
// Simple speech recognition service based on studiodev TranslationService
// Used by the Translation Tools -> AI Captions feature

import { getLanguageById } from './languageConfig';

// API base URL for translation
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Translate text using backend translation API
 */
export async function translateText(text, sourceLang, targetLang) {
  if (sourceLang === targetLang || !text.trim()) {
    return text;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
        sourceLang,
        targetLang,
      }),
    });

    if (!response.ok) {
      throw new Error(`Translation failed: ${response.status}`);
    }

    const data = await response.json();
    return data.translation || text;
  } catch (error) {
    console.error('[AI Captions] Translation error:', error);
    return text;
  }
}

/**
 * Speech recognition using Web Speech API
 * Simple implementation matching studiodev
 */
export function createSpeechRecognition(languageCode, onResult, onError) {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

  if (!SpeechRecognition) {
    return {
      isSupported: false,
      start: () => onError?.('Speech recognition not supported in this browser'),
      stop: () => {},
    };
  }

  const language = getLanguageById(languageCode);
  let recognition = new SpeechRecognition();
  let currentLang = language?.code || 'en-US';
  let isRunning = false;

  const setupRecognition = () => {
    recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = currentLang;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const resultIndex = event.resultIndex;
      const result = event.results[resultIndex];

      if (result) {
        console.log('[AI Captions] Result:', result[0].transcript, 'Final:', result.isFinal);
        onResult({
          text: result[0].transcript,
          isFinal: result.isFinal,
          confidence: result[0].confidence,
          timestamp: Date.now()
        });
      }
    };

    recognition.onerror = (event) => {
      console.log('[AI Captions] Error:', event.error);
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        if (event.error === 'not-allowed') {
          onError?.('Microphone access denied. Please allow microphone access in your browser settings.');
        } else if (event.error === 'audio-capture') {
          onError?.('No microphone found. Please connect a microphone.');
        }
      }
    };

    recognition.onend = () => {
      console.log('[AI Captions] Recognition ended, isRunning:', isRunning);
      // Auto-restart if still supposed to be running
      if (isRunning) {
        setTimeout(() => {
          if (isRunning) {
            try {
              recognition.start();
              console.log('[AI Captions] Restarted');
            } catch (e) {
              console.log('[AI Captions] Restart failed, recreating...');
              setupRecognition();
              try {
                recognition.start();
              } catch (e2) {
                console.warn('[AI Captions] Failed to restart');
              }
            }
          }
        }, 100);
      }
    };
  };

  setupRecognition();

  return {
    isSupported: true,
    start: () => {
      console.log('[AI Captions] Starting recognition for language:', currentLang);
      isRunning = true;
      try {
        recognition.start();
      } catch (e) {
        console.log('[AI Captions] Start error, recreating...');
        setupRecognition();
        try {
          recognition.start();
        } catch (e2) {
          onError?.('Failed to start speech recognition');
        }
      }
    },
    stop: () => {
      console.log('[AI Captions] Stopping');
      isRunning = false;
      try {
        recognition.stop();
      } catch (e) {
        // Already stopped
      }
    },
    setLanguage: (langCode) => {
      const lang = getLanguageById(langCode);
      currentLang = lang?.code || 'en-US';
      console.log('[AI Captions] Language changed to:', currentLang);
      if (isRunning) {
        try {
          recognition.stop();
        } catch (e) {}
        setupRecognition();
        try {
          recognition.start();
        } catch (e) {}
      }
    }
  };
}

/**
 * Full caption session with speech recognition + optional translation
 */
export function createAICaptionSession({
  spokenLanguage: initialSpokenLanguage,
  captionLanguage: initialCaptionLanguage,
  onCaption,
  onError
}) {
  let lastFinalText = '';
  let currentSpokenLanguage = initialSpokenLanguage;
  let currentCaptionLanguage = initialCaptionLanguage;

  const handleSpeechResult = async (result) => {
    const needsTranslation = currentSpokenLanguage !== currentCaptionLanguage;

    // For interim results
    if (!result.isFinal) {
      onCaption({
        text: result.text,
        isFinal: false,
        isTranslating: needsTranslation,
        timestamp: result.timestamp
      });
      return;
    }

    // Skip empty or duplicate results
    if (!result.text.trim() || result.text === lastFinalText) return;
    lastFinalText = result.text;

    // If same language, no translation needed
    if (!needsTranslation) {
      onCaption({
        text: result.text,
        isFinal: true,
        isTranslating: false,
        timestamp: result.timestamp
      });
      return;
    }

    // Show original with translating indicator
    onCaption({
      text: result.text,
      originalText: result.text,
      isFinal: false,
      isTranslating: true,
      showTranslatingIndicator: true,
      timestamp: result.timestamp
    });

    // Translate asynchronously
    translateText(result.text, currentSpokenLanguage, currentCaptionLanguage)
      .then(translated => {
        onCaption({
          text: translated,
          originalText: result.text,
          isFinal: true,
          isTranslating: false,
          wasTranslated: translated !== result.text,
          timestamp: result.timestamp
        });
      })
      .catch(error => {
        console.error('[AI Captions] Translation failed:', error);
        onCaption({
          text: result.text,
          isFinal: true,
          isTranslating: false,
          error: true,
          timestamp: result.timestamp
        });
      });
  };

  const recognition = createSpeechRecognition(
    currentSpokenLanguage,
    handleSpeechResult,
    onError
  );

  return {
    isSupported: recognition.isSupported,
    start: recognition.start,
    stop: recognition.stop,
    updateLanguages: (spoken, caption) => {
      currentSpokenLanguage = spoken;
      currentCaptionLanguage = caption;
      recognition.setLanguage?.(spoken);
    }
  };
}
